/**
 * Product Horizontal Slider
 *
 * Enhances a <product-images data-horizontal-desktop> element with:
 *  - Prev/next arrow buttons
 *  - Dot pagination indicators
 *  - Keyboard arrow navigation
 *  - Sync with theme:media:select (e.g. variant image change)
 *  - Variant dedup: all variant-attached slides collapse into a single
 *    "variant slot" (the first scroll position). Selecting a different
 *    variant cross-fades the slot image instead of inserting an extra slide.
 *  - PhotoSwipe lightbox filter: when the user opens the fullscreen
 *    lightbox, only the visible images (active variant + non-variant)
 *    are included in the slides and thumbs panel.
 *
 * The horizontal scrolling itself is handled by native CSS scroll-snap
 * (see .product__images--horizontal in theme.css). This script only
 * adds the navigation chrome and orchestration around it.
 *
 * Click-to-fullscreen (PhotoSwipe via zoom.js) is layout-agnostic and
 * continues to work because we never intercept clicks on .product__slide.
 */
(function () {
  'use strict';

  const DESKTOP_BREAKPOINT = 750;

  const SELECTORS = {
    component: 'product-images[data-horizontal-desktop]',
    scroller: '[data-product-media-list]',
    slide: '.product__slide',
    variantSlide: '.product__slide[data-variant-image]',
    nonVariantSlide: '.product__slide:not([data-variant-image])',
    zoomImages: 'zoom-images',
    zoomImage: '[data-zoom-image]',
    pswpThumbs: '.pswp__thumbs',
    variantThumb: '[data-variant-thumb]',
  };

  const ATTRS = {
    mediaId: 'data-media-id',
    variantImage: 'data-variant-image',
    activeMedia: 'data-active-media',
  };

  const CLASSES = {
    arrows: 'product__horizontal-arrows',
    arrow: 'product__horizontal-arrow',
    arrowPrev: 'product__horizontal-arrow--prev',
    arrowNext: 'product__horizontal-arrow--next',
    dots: 'product__horizontal-dots',
    dot: 'product__horizontal-dot',
    dotActive: 'is-active',
    mediaHidden: 'media--hidden',
    mediaHiding: 'media--hiding',
  };

  const ARROW_ICON_PREV =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>';
  const ARROW_ICON_NEXT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>';

  const instances = new WeakMap();

  function isDesktop() {
    return window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`).matches;
  }

  function isVariantHidden(slide) {
    return (
      slide.classList.contains(CLASSES.mediaHidden) ||
      slide.classList.contains(CLASSES.mediaHiding)
    );
  }

  class ProductHorizontalSlider {
    constructor(component) {
      this.component = component;
      this.scroller = component.querySelector(SELECTORS.scroller);
      if (!this.scroller) return;

      this.variantSlides = Array.from(
        this.scroller.querySelectorAll(SELECTORS.variantSlide)
      );
      this.nonVariantSlides = Array.from(
        this.scroller.querySelectorAll(SELECTORS.nonVariantSlide)
      );

      // Visible-position layout:
      //   if there is at least one variant slide, position 0 = variant slot;
      //   positions 1..N = non-variant slides (in DOM order).
      // Otherwise positions 0..N-1 = non-variant slides.
      this.hasVariantSlot = this.variantSlides.length > 0;
      this.positionCount =
        (this.hasVariantSlot ? 1 : 0) + this.nonVariantSlides.length;

      if (this.positionCount <= 1) return;

      this.active = false;
      this.activeIndex = 0;
      this.rafId = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onMediaSelect = this.onMediaSelect.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);

      this.build();
      this.patchZoom();

      window.addEventListener('resize', this.onResize, { passive: true });
      this.component.addEventListener('theme:media:select', this.onMediaSelect);

      this.onResize();
    }

    build() {
      this.arrowsWrap = document.createElement('div');
      this.arrowsWrap.className = CLASSES.arrows;

      this.prevButton = document.createElement('button');
      this.prevButton.type = 'button';
      this.prevButton.className = `${CLASSES.arrow} ${CLASSES.arrowPrev}`;
      this.prevButton.setAttribute('aria-label', 'Previous image');
      this.prevButton.innerHTML = ARROW_ICON_PREV;
      this.prevButton.addEventListener('click', this.onPrevClick);

      this.nextButton = document.createElement('button');
      this.nextButton.type = 'button';
      this.nextButton.className = `${CLASSES.arrow} ${CLASSES.arrowNext}`;
      this.nextButton.setAttribute('aria-label', 'Next image');
      this.nextButton.innerHTML = ARROW_ICON_NEXT;
      this.nextButton.addEventListener('click', this.onNextClick);

      this.arrowsWrap.appendChild(this.prevButton);
      this.arrowsWrap.appendChild(this.nextButton);
      this.component.appendChild(this.arrowsWrap);

      this.dotsList = document.createElement('ol');
      this.dotsList.className = CLASSES.dots;
      this.dotsList.setAttribute('role', 'tablist');

      this.dots = [];
      for (let i = 0; i < this.positionCount; i++) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = CLASSES.dot;
        button.setAttribute('aria-label', `Go to image ${i + 1}`);
        button.setAttribute('role', 'tab');
        button.addEventListener('click', () => this.scrollToIndex(i));
        item.appendChild(button);
        this.dotsList.appendChild(item);
        this.dots.push(button);
      }

      this.component.appendChild(this.dotsList);
    }

    onResize() {
      const shouldActivate = isDesktop();
      if (shouldActivate && !this.active) {
        this.activate();
      } else if (!shouldActivate && this.active) {
        this.deactivate();
      }
      if (this.active) {
        this.update();
      }
    }

    activate() {
      this.active = true;
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.component.addEventListener('keydown', this.onKeyDown);
      this.arrowsWrap.hidden = false;
      this.dotsList.hidden = false;
    }

    deactivate() {
      this.active = false;
      this.scroller.removeEventListener('scroll', this.onScroll);
      this.component.removeEventListener('keydown', this.onKeyDown);
      this.arrowsWrap.hidden = true;
      this.dotsList.hidden = true;
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      const width = this.scroller.clientWidth || 1;
      const index = Math.round(this.scroller.scrollLeft / width);
      const clamped = Math.max(0, Math.min(this.positionCount - 1, index));
      this.setActiveIndex(clamped);
      this.updateArrowState();
    }

    setActiveIndex(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle(CLASSES.dotActive, i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    updateArrowState() {
      const max = this.positionCount - 1;
      this.prevButton.disabled = this.activeIndex <= 0;
      this.nextButton.disabled = this.activeIndex >= max;
    }

    /**
     * Map a visible-position index to a target scrollLeft.
     * Position 0 is the variant slot (when present) which lives at scrollLeft = 0.
     * Higher positions correspond to non-variant slides in DOM order; we use
     * their actual `offsetLeft` for accuracy regardless of layout quirks.
     */
    scrollToIndex(index) {
      let left = 0;
      if (this.hasVariantSlot) {
        if (index === 0) {
          left = 0;
        } else {
          const slide = this.nonVariantSlides[index - 1];
          if (!slide) return;
          left = slide.offsetLeft;
        }
      } else {
        const slide = this.nonVariantSlides[index];
        if (!slide) return;
        left = slide.offsetLeft;
      }
      this.scroller.scrollTo({ left, behavior: 'smooth' });
    }

    onPrevClick(event) {
      event.preventDefault();
      this.scrollToIndex(Math.max(0, this.activeIndex - 1));
    }

    onNextClick(event) {
      event.preventDefault();
      this.scrollToIndex(Math.min(this.positionCount - 1, this.activeIndex + 1));
    }

    onKeyDown(event) {
      if (event.code === 'ArrowLeft') {
        event.preventDefault();
        this.scrollToIndex(Math.max(0, this.activeIndex - 1));
      } else if (event.code === 'ArrowRight') {
        event.preventDefault();
        this.scrollToIndex(Math.min(this.positionCount - 1, this.activeIndex + 1));
      }
    }

    onMediaSelect(event) {
      const id = event.detail && event.detail.id;
      if (!id) return;
      // Locate the slide for this media id.
      const slide = this.scroller.querySelector(
        `${SELECTORS.slide}[${ATTRS.mediaId}="${id}"]`
      );
      if (!slide) return;

      if (slide.hasAttribute(ATTRS.variantImage)) {
        // Variant change: keep the user wherever they are scrolled; the
        // CSS cross-fade handles the swap in the variant slot. We only
        // need to make sure the variant-slot dot reflects the swap when
        // the user is parked at position 0.
        if (this.activeIndex === 0) {
          this.setActiveIndex(0);
          this.updateArrowState();
        }
        return;
      }

      // Non-variant slide: jump the indicator/scroll to that slide.
      const nonVariantIndex = this.nonVariantSlides.indexOf(slide);
      if (nonVariantIndex < 0) return;
      const targetIndex = (this.hasVariantSlot ? 1 : 0) + nonVariantIndex;
      if (this.active) {
        this.scrollToIndex(targetIndex);
      } else {
        this.setActiveIndex(targetIndex);
        this.updateArrowState();
      }
    }

    /**
     * Override <zoom-images>.createZoom so the PhotoSwipe lightbox shows
     * only the currently-visible images (active variant + non-variant).
     *
     * Two pieces need filtering and they must produce the SAME order so
     * thumbs-click indexes line up with PhotoSwipe slide indexes:
     *
     *  1. The PhotoSwipe slides array (built from `this.images` inside
     *     createZoom). We temporarily swap `this.images` with a filtered
     *     Array before calling the original createZoom, then restore it.
     *
     *  2. The thumbs strip displayed below the lightbox. zoom.js populates
     *     `.pswp__thumbs` synchronously inside createZoom (it sets
     *     `this.thumbsContainer.innerHTML = template.innerHTML`). PhotoSwipe
     *     itself boots asynchronously and only later calls `thumbsActions()`
     *     which queries those children and binds click handlers. We exploit
     *     that gap: as soon as `originalCreateZoom` returns, the container
     *     is freshly populated but no microtasks have run yet, so we prune
     *     inactive variant thumbs IN-PLACE. By the time thumbsActions runs,
     *     the strip has been trimmed to (1 active variant + N non-variants)
     *     and its click handlers get bound with indexes that line up with
     *     the filtered PhotoSwipe slides.
     */
    patchZoom() {
      const zoomEl = this.component.querySelector(SELECTORS.zoomImages);
      if (!zoomEl || zoomEl.__horizontalZoomPatched) return;

      const allImages = zoomEl.images;
      if (!allImages) return;

      const componentEl = this.component;

      const visibleImagesNow = () => {
        return Array.from(allImages).filter((img) => {
          const slide = img.closest(SELECTORS.slide);
          if (!slide) return true;
          if (!slide.hasAttribute(ATTRS.variantImage)) return true;
          return !isVariantHidden(slide);
        });
      };

      const pruneThumbs = () => {
        const container = document.querySelector(SELECTORS.pswpThumbs);
        if (!container) return;
        const activeId = componentEl.getAttribute(ATTRS.activeMedia);
        const thumbs = container.querySelectorAll(SELECTORS.variantThumb);
        thumbs.forEach((thumb) => {
          if (thumb.getAttribute(ATTRS.mediaId) !== activeId) {
            thumb.parentNode.removeChild(thumb);
          }
        });
      };

      const originalCreateZoom = zoomEl.createZoom.bind(zoomEl);

      zoomEl.createZoom = function (clickedIndex) {
        const filtered = visibleImagesNow();
        const clickedNode = allImages[clickedIndex];
        let remappedIndex = filtered.indexOf(clickedNode);
        if (remappedIndex < 0) remappedIndex = 0;

        // Temporarily swap `this.images` so the original createZoom builds
        // its PhotoSwipe slides array from the filtered set. createZoom
        // captures the slides array synchronously, so restoring afterwards
        // is safe.
        const originalImages = this.images;
        try {
          this.images = filtered;
          originalCreateZoom(remappedIndex);
        } finally {
          this.images = originalImages;
        }

        // At this point the original createZoom has just synchronously set
        // `.pswp__thumbs` innerHTML to the full template (32 thumbs). No
        // microtasks have run yet, so PhotoSwipe's async `loadPopup` ->
        // `thumbsActions` chain hasn't kicked in. Prune now so the strip
        // is trimmed before any click handlers get bound to it.
        pruneThumbs();
      };

      zoomEl.__horizontalZoomPatched = true;
    }

    destroy() {
      this.deactivate();
      window.removeEventListener('resize', this.onResize);
      this.component.removeEventListener('theme:media:select', this.onMediaSelect);
      if (this.arrowsWrap && this.arrowsWrap.parentNode) {
        this.arrowsWrap.parentNode.removeChild(this.arrowsWrap);
      }
      if (this.dotsList && this.dotsList.parentNode) {
        this.dotsList.parentNode.removeChild(this.dotsList);
      }
    }
  }

  function init(scope) {
    const root = scope || document;
    const components = root.querySelectorAll(SELECTORS.component);
    components.forEach((component) => {
      if (instances.has(component)) return;
      const instance = new ProductHorizontalSlider(component);
      instances.set(component, instance);
    });
  }

  function destroy(scope) {
    const root = scope || document;
    const components = root.querySelectorAll(SELECTORS.component);
    components.forEach((component) => {
      const instance = instances.get(component);
      if (instance) {
        instance.destroy();
        instances.delete(component);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });
})();
